import { useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { NotificationBell } from '../NotificationBell'
import {
  Home,
  Users,
  GraduationCap,
  Calendar,
  DollarSign,
  FileText,
  Menu,
  X,
  LogOut,
  Sun,
  Moon,
  User,
  UserCog,
  SquareKanban
} from 'lucide-react'
import { canUseBoards, canUseTutoringPages, hasAdminRights, roleLabel } from '../../lib/roles'

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [darkMode, setDarkMode] = useState(true)
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const toggleDarkMode = () => {
    setDarkMode(!darkMode)
    if (!darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/sign-in')
  }

  const role = profile?.role
  const navigation = [
    ...(hasAdminRights(role) ? [{ name: 'Dashboard', href: '/dashboard', icon: Home }] : []),
    ...(canUseBoards(role) ? [{ name: 'Client Boards', href: '/dashboard/boards', icon: SquareKanban }] : []),
    ...(hasAdminRights(role) ? [{ name: 'Tutors', href: '/dashboard/tutors', icon: GraduationCap }] : []),
    ...(canUseTutoringPages(role)
      ? [
          { name: 'Students', href: '/dashboard/students', icon: Users },
          { name: 'Sessions', href: '/dashboard/sessions', icon: Calendar },
        ]
      : []),
    ...(canUseBoards(role) ? [{ name: 'Top-ups', href: '/dashboard/topups', icon: DollarSign }] : []),
    ...(canUseTutoringPages(role) ? [{ name: 'Reports', href: '/dashboard/reports', icon: FileText }] : []),
    ...(hasAdminRights(role) ? [{ name: 'User Management', href: '/dashboard/users', icon: UserCog }] : []),  ]

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-surface-0">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center">
              <img
                src="/TMM-logo.png"
                alt="TMM Logo"
                className="w-16 h-16 mr-3 object-contain"
              />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">MyTutor+</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                    isActive
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-800">
            <Link
              to="/dashboard/profile"
              className="flex items-center gap-3 px-2 py-2 mb-2 rounded-md bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {profile?.name || profile?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {roleLabel(profile?.role)}
                </p>
              </div>
            </Link>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 w-full px-2 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/50 rounded-md transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white dark:bg-surface-100 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1 flex items-center justify-center lg:hidden">
              <img
                src="/TMM-logo.png"
                alt="TMM Logo"
                className="w-12 h-12 object-contain"
              />
            </div>

            <div className="flex-1 hidden lg:block"></div>

            <div className="flex items-center gap-1">
              <NotificationBell />
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-surface-0 transition-colors"
              >
                {darkMode ? (
                  <Sun className="h-5 w-5 text-yellow-500" />
                ) : (
                  <Moon className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
